import { useMemo, useState } from "react";
import {
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { message as antdMessage } from "antd";

import {
  generatedDocumentArtifactFromToolCall,
  type GeneratedDocumentArtifact,
} from "../../../pages/Neo/generatedDocumentArtifact";
import { openProjectDocument } from "../../../pages/Neo/openProjectDocument";
import ActionProposalCard, {
  executionIdFromProposeActionOutput,
  proposeActionMetaFromOutput,
  proposeActionParamsFromInput,
} from "../../../pages/Neo/ActionProposalCard";
import { openVoiceCallModal } from "../../../pages/Neo/VoiceCallTranscriptModal";
import type { CopilotToolCall } from "./types";
import styles from "./styles.module.scss";

const formatBytes = (bytes?: number): string => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const GeneratedDocumentCard = ({
  artifact,
}: {
  artifact: GeneratedDocumentArtifact;
}) => {
  const [downloading, setDownloading] = useState(false);
  const formatLabel = artifact.format === "pdf" ? "PDF" : "Excel";

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await openProjectDocument({
        fileId: artifact.fileId,
        name: artifact.filename,
        mode: "download",
      });
    } catch (error) {
      const detail =
        error instanceof Error ? `: ${error.message}` : ". Please try again.";
      antdMessage.error(`Could not download ${formatLabel}${detail}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section className={styles.generatedFileCard}>
      <span className={styles.generatedFileIcon} aria-hidden="true">
        {artifact.format === "pdf" ? (
          <FilePdfOutlined />
        ) : (
          <FileExcelOutlined />
        )}
      </span>
      <span className={styles.generatedFileDetails}>
        <strong title={artifact.filename}>{artifact.filename}</strong>
        <span>
          {formatLabel}
          {artifact.sizeBytes ? ` · ${formatBytes(artifact.sizeBytes)}` : ""}
        </span>
      </span>
      <button
        type="button"
        className={styles.generatedFileDownload}
        onClick={() => void download()}
        disabled={downloading}
      >
        <DownloadOutlined />
        {downloading ? "Preparing…" : "Download"}
      </button>
    </section>
  );
};

const GeneratedDocumentCards = ({ tools }: { tools: CopilotToolCall[] }) => {
  const artifacts = useMemo(() => {
    const byFileId = new Map<number, GeneratedDocumentArtifact>();
    tools.forEach((tool) => {
      const artifact = generatedDocumentArtifactFromToolCall({
        toolName: tool.name,
        status: tool.status,
        isError: tool.isError,
        input: tool.input,
        output: tool.output,
      });
      if (artifact) byFileId.set(artifact.fileId, artifact);
    });
    return [...byFileId.values()];
  }, [tools]);
  const proposals = useMemo(
    () =>
      tools.flatMap((tool) => {
        if (
          tool.name !== "propose_action" ||
          tool.status !== "completed" ||
          tool.isError
        ) {
          return [];
        }
        const executionId = executionIdFromProposeActionOutput(tool.output);
        if (executionId == null) return [];
        return [
          {
            executionId,
            ...proposeActionMetaFromOutput(tool.output),
            params: proposeActionParamsFromInput(tool.input),
          },
        ];
      }),
    [tools]
  );
  const voiceCalls = useMemo(
    () =>
      tools.flatMap((tool) => {
        if (
          tool.name !== "get_voice_call" ||
          tool.status !== "completed" ||
          tool.isError ||
          !tool.output ||
          typeof tool.output !== "object"
        ) {
          return [];
        }
        const fields = (tool.output as { fields?: Record<string, unknown> })
          .fields;
        return typeof fields?.conversation_id === "string" &&
          fields.conversation_id
          ? [fields.conversation_id]
          : [];
      }),
    [tools]
  );

  if (
    artifacts.length === 0 &&
    proposals.length === 0 &&
    voiceCalls.length === 0
  ) {
    return null;
  }
  return (
    <div className={styles.generatedFiles}>
      {artifacts.map((artifact) => (
        <GeneratedDocumentCard key={artifact.fileId} artifact={artifact} />
      ))}
      {proposals.map((proposal) => (
        <ActionProposalCard
          key={proposal.executionId}
          executionId={proposal.executionId}
          actionName={proposal.actionName}
          initialStatus={proposal.status}
          clinicId={proposal.clinicId}
          params={proposal.params}
        />
      ))}
      {voiceCalls.map((conversationId) => (
        <button
          key={conversationId}
          type="button"
          className={styles.voiceCallCard}
          onClick={() => openVoiceCallModal(conversationId)}
        >
          <span>
            <small>Voice call</small>
            <strong>Call transcript and recording</strong>
          </span>
          <b>Open call →</b>
        </button>
      ))}
    </div>
  );
};

export default GeneratedDocumentCards;
